import { useMemo, useRef, useState } from 'react'
import { LuBuilding2, LuSearch, LuSearchX, LuX } from 'react-icons/lu'

import { toaster } from '@/components/Toaster'
import { useGetUser } from '@/feature/auth'

import { Button } from '@/lib/core/components/Button'
import { GET_ALL_LIMIT } from '@/lib/core/constants/constants'
import { usePopover } from '@/lib/core/hooks/usePopover'

import { useMyContacts } from '@/lib/features/Contacts/api'

import { AssignOrgPayload, useAssignOrg } from '../../api'
import { TaskDefinition } from '../../types/task'
import { TaskStatus } from '../CreateTaskModal'
import styles from '../TaskCellPopout.module.css'

interface AssignedCompanyPopoutProps {
  task: TaskDefinition
  testId?: string
  onUpdated: () => void
}

export const AssignedCompanyPopout = ({ task, testId, onUpdated }: AssignedCompanyPopoutProps) => {
  const computedTestId = testId ?? `assigned-company-popout-${task.taskDefinition.id}`

  const { trigger: assignOrg } = useAssignOrg(task.taskDefinition.id)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const { positionPopover } = usePopover({
    buttonRef,
    dialogRef,
    defaultPopoverDimensions: { width: 324, height: 275 },
  })
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const { data: contactsData } = useMyContacts({ pageSize: GET_ALL_LIMIT })
  const { data: currentUser } = useGetUser()

  const contacts = useMemo(() => {
    return contactsData.results.sort((a, b) =>
      a.organizationTo.name.localeCompare(b.organizationTo.name, 'en', {
        caseFirst: 'upper',
        numeric: true,
      }),
    )
  }, [contactsData])

  const searchFilteredContacts = useMemo(() => {
    if (!searchTerm.trim()) {
      return contacts
    }

    return contacts.filter((contact) =>
      contact.organizationTo.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [contacts, searchTerm])

  const showPopover = (e: React.MouseEvent) => {
    e.stopPropagation()
    positionPopover()
    dialogRef.current?.showPopover()
  }

  const handleUpdateAssignedCompany = async (orgId: number) => {
    const payload: AssignOrgPayload = { assignedOrg: orgId, predecessor: task.id }

    try {
      await assignOrg(payload)
      toaster({
        type: 'success',
        message: 'Task assigned company updated',
      })
      onUpdated()
      dialogRef.current?.hidePopover()
    } catch {
      toaster({
        type: 'error',
        message: 'Failed to update task assigned company. Please try again.',
      })
    }
  }

  const isDisabled = useMemo(() => {
    return (
      [TaskStatus.COMPLETED, TaskStatus.IN_PROGRESS].includes(task.status as TaskStatus) ||
      !!task.assignedVehicle
    )
  }, [task])

  const companyName = useMemo(() => {
    if (task.onwardAssignedToOrg) {
      return task.onwardAssignedToOrg.name
    }
    // if the task has not been onward assigned, it show reflect as
    // assigned to my company
    return currentUser?.organization?.name
  }, [task, currentUser])

  return (
    <div data-testid={`${computedTestId}-container`}>
      <div className={`${styles.taskCellPopout}`.trim()} data-testid={computedTestId}>
        <Button
          ref={buttonRef}
          variant="ghost"
          leftIcon={<LuBuilding2 size={16} />}
          className={styles.taskCellPopoutButton}
          onClick={showPopover}
          disabled={isDisabled}
          data-testid={`${computedTestId}-trigger-button`}
        >
          <div className={styles.taskCellPopoutButtonLabel}>{companyName}</div>
        </Button>

        <dialog
          id={`assigned-company-dialog-${task.taskDefinition.id}`}
          ref={dialogRef}
          className={styles.popoutContainer}
          onClick={() => dialogRef.current?.close()}
          popover="auto"
        >
          <div className={styles.popoutWrapper}>
            <div className={styles.popoutHeader}>
              <div className={styles.searchContainer}>
                <input
                  ref={searchInputRef}
                  data-testid={`assigned-company-dialog-${task.taskDefinition.id}-search-field`}
                  type="text"
                  placeholder="Search Company"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                <button
                  className={styles.searchIconButton}
                  data-testid={`assigned-company-dialog-${task.taskDefinition.id}-search-icon`}
                  onClick={() => {
                    if (searchTerm) {
                      setSearchTerm('')
                    }
                    searchInputRef.current?.focus()
                  }}
                >
                  {searchTerm ? (
                    <LuSearchX size={18} />
                  ) : (
                    <LuSearch size={18} />
                  )}
                </button>
              </div>
              <div className={styles.headerActions}>
                <button
                  className={styles.actionButton}
                  data-testid={`assigned-company-dialog-${task.taskDefinition.id}-close`}
                  onClick={() => {
                    setSearchTerm('')
                    dialogRef.current?.hidePopover()
                  }}
                >
                  <LuX size={16} />
                </button>
              </div>
            </div>

            <div className={styles.optionsWrapper}>
              {searchFilteredContacts.map((contact) => (
                <div
                  key={`assigned-to-${contact.organizationTo.id}`}
                  onClick={() => handleUpdateAssignedCompany(contact.organizationTo.id)}
                  className={styles.option}
                >
                  {contact.organizationTo.name}
                </div>
              ))}
            </div>
          </div>
        </dialog>
      </div>
    </div>
  )
}
